/**
 * Settings Page Component (TASK-051)
 *
 * Main settings page with tabs:
 * - Organization Settings
 * - Integrations
 * - System Settings
 * - Subscription & Billing (hidden, TODO: implement later)
 */
import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { settingsApi } from '@/services/api/settings';
import { brandingApi } from '@/services/api/branding';
import apiClient from '@/services/api/client';
import { formatFieldErrors } from '@/lib/fieldErrors';
import { prepareAttributes, validateAttributes } from '@/lib/leadAttributes';
import { ORGANIZATION_FIELD_LABELS } from '@/lib/organizationContacts';
import type {
  Settings,
  OrganizationSettings as OrgSettingsType,
  IntegrationsSettings as IntegrationsSettingsType,
  SystemSettings as SystemSettingsType,
  UpdateOrganizationPayload,
  UpdateIntegrationsPayload,
  UpdateSystemPayload,
} from '@/types/settings';
import { Settings as SettingsIcon } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { OrganizationSettings } from '@/components/settings/OrganizationSettings';
import { IntegrationSettings } from '@/components/settings/IntegrationSettings';
import { SystemSettings } from '@/components/settings/SystemSettings';
import { FunnelConfig } from '@/components/funnel/FunnelConfig';
import { EmbeddedPage } from '@/components/layout/PageLayout';
import { TelephonyPage } from '@/pages/TelephonyPage';
import { TelegramAccountsPage } from '@/pages/TelegramAccountsPage';
import { MaxAccountsSection } from '@/components/max/MaxAccountsSection';
import { MAX_ACCOUNTS_URL } from '@/services/api/maxAccountsUrl';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { ProxiesBlock } from '@/components/telegram/ProxiesBlock';

const DEFAULT_TAB = 'organization';
/**
 * KAN-718: старые адреса вкладок, которые переехали внутрь других.
 * ?tab=mail: ящик для переписки теперь секция на вкладке "Интеграции".
 */
const TAB_ALIASES: Record<string, string> = { mail: 'integrations' };
/**
 * Loading spinner component
 */
function LoadingSpinner() {
  return (
    <div data-testid="settings-loading" className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  );
}

/**
 * Error state component
 */
function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div data-testid="settings-error" className="flex items-center justify-center h-64">
      <div className="text-center">
        <p className="text-red-600 font-medium">Ошибка загрузки настроек</p>
        <p className="text-gray-500 text-sm mt-1">Попробуйте позже</p>
        <Button data-testid="retry-button" onClick={onRetry} className="mt-4" variant="outline">
          Повторить
        </Button>
      </div>
    </div>
  );
}

/**
 * Read-only notice component
 */
function ReadOnlyNotice() {
  return (
    <div
      data-testid="read-only-notice"
      className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md mb-4">
      У вас нет прав для изменения настроек. Режим только для чтения.
    </div>
  );
}

/**
 * Settings Page Component
 */
export function SettingsPage() {
  const queryClient = useQueryClient();
  // Вкладка живёт в URL (?tab=telephony): на неё ведут старые адреса
  // /telephony и /telegram-accounts, ссылку на вкладку можно переслать.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') || DEFAULT_TAB;
  const activeTab = TAB_ALIASES[requestedTab] ?? requestedTab;
  const focusMailbox = requestedTab === 'mail';
  const setActiveTab = useCallback(
    (tab: string) => setSearchParams(tab === DEFAULT_TAB ? {} : { tab }, { replace: true }),
    [setSearchParams]
  );
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingTab, setPendingTab] = useState<string | null>(null);

  // Form state
  const [orgForm, setOrgForm] = useState<Partial<OrgSettingsType>>({});
  const [integrationsForm, setIntegrationsForm] = useState<Partial<IntegrationsSettingsType>>({});
  const [systemForm, setSystemForm] = useState<Partial<SystemSettingsType>>({});

  // Dirty state tracking
  const [isDirty, setIsDirty] = useState(false);

  // Error states
  const [orgNameError, setOrgNameError] = useState<string | null>(null);
  const [paginationError, setPaginationError] = useState<string | null>(null);
  const [_webhookUrlError, setWebhookUrlError] = useState<string | null>(null);

  // Set document title
  useEffect(() => {
    document.title = 'Настройки - SMETA AI';
  }, []);

  // Fetch settings
  const {
    data: settings,
    isLoading,
    isError,
    refetch,
  } = useQuery<Settings>({
    queryKey: ['settings'],
    queryFn: settingsApi.getSettings,
  });

  // Initialize form data when settings load
  useEffect(() => {
    if (settings) {
      setOrgForm(settings.organization);
      setIntegrationsForm(settings.integrations);
      setSystemForm(settings.system);
    }
  }, [settings]);

  // Update mutations
  const updateOrganizationMutation = useMutation({
    mutationFn: (payload: UpdateOrganizationPayload) => settingsApi.updateOrganization(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      showSuccess('Настройки организации сохранены');
      setIsDirty(false);
    },
    // KAN-742: show the field validation errors (e.g. a bad website or Telegram).
    onError: (error: unknown) => {
      toast.error(
        formatFieldErrors(error, ORGANIZATION_FIELD_LABELS, 'Не удалось сохранить настройки организации')
      );
    },
  });

  const updateIntegrationsMutation = useMutation({
    mutationFn: (payload: UpdateIntegrationsPayload) => settingsApi.updateIntegrations(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      showSuccess('Настройки интеграций сохранены');
      setIsDirty(false);
    },
  });

  const updateSystemMutation = useMutation({
    mutationFn: (payload: UpdateSystemPayload) => settingsApi.updateSystem(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      showSuccess('Системные настройки сохранены');
      setIsDirty(false);
    },
  });

  const uploadLogoMutation = useMutation({
    mutationFn: brandingApi.uploadLogo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      showSuccess('Логотип загружен');
    },
    onError: () => {
      toast.error('Ошибка загрузки логотипа');
    },
  });

  const handleLogoUpload = useCallback(
    (file: File) => {
      uploadLogoMutation.mutate(file);
    },
    [uploadLogoMutation]
  );

  // Show success toast
  const showSuccess = useCallback((message: string) => {
    toast.success(message);
  }, []);

  // Handle tab change with dirty check
  const handleTabChange = useCallback(
    (newTab: string) => {
      if (isDirty) {
        setPendingTab(newTab);
        setShowUnsavedDialog(true);
      } else {
        setActiveTab(newTab);
      }
    },
    [isDirty, setActiveTab]
  );

  // Handle discard changes
  const handleDiscardChanges = useCallback(() => {
    if (settings) {
      setOrgForm(settings.organization);
      setIntegrationsForm(settings.integrations);
      setSystemForm(settings.system);
    }
    setIsDirty(false);
    setShowUnsavedDialog(false);
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
  }, [settings, pendingTab, setActiveTab]);

  // Form update handlers
  const handleOrgChange = useCallback((updates: Partial<OrgSettingsType>) => {
    setOrgForm(prev => ({ ...prev, ...updates }));
    setIsDirty(true);
    if (updates.name !== undefined) {
      setOrgNameError(null);
    }
  }, []);

  const handleIntegrationsChange = useCallback((updates: Partial<IntegrationsSettingsType>) => {
    setIntegrationsForm(prev => ({ ...prev, ...updates }));
    setIsDirty(true);
    if (updates.webhook_url !== undefined) {
      setWebhookUrlError(null);
    }
  }, []);

  const handleSystemChange = useCallback((updates: Partial<SystemSettingsType>) => {
    setSystemForm(prev => ({ ...prev, ...updates }));
    setIsDirty(true);
    if (updates.pagination_size !== undefined) {
      setPaginationError(null);
    }
  }, []);

  // Save handlers
  const handleSaveOrganization = useCallback(() => {
    // Validate
    if (!orgForm.name || orgForm.name.trim() === '') {
      setOrgNameError('Nastroika organizatsii obyazatelna');
      return;
    }
    // KAN-783: lead attributes with an empty or repeated name are not sent;
    // the hint is shown under the field by the editor itself.
    const leadAttributes = orgForm.lead_attributes;
    if (
      leadAttributes &&
      Object.keys(validateAttributes(leadAttributes, orgForm.lead_attribute_reserved_names)).length > 0
    ) {
      toast.error('Не сохранено: проверьте атрибуты в блоке "Что бот выясняет у клиента"');
      return;
    }
    // TASK-135: Include company_description in the update payload
    // company_name: short name spoken by the bot in greetings
    // TASK-270: mode + ecommerce_settings (e-commerce module knobs)
    updateOrganizationMutation.mutate({
      name: orgForm.name,
      company_name: orgForm.company_name,
      company_description: orgForm.company_description,
      mode: orgForm.mode,
      ecommerce_settings: orgForm.ecommerce_settings,
      // KAN-742: organization contacts (КОНТАКТЫ section of the bot prompt)
      website: orgForm.website,
      phone: orgForm.phone,
      company_email: orgForm.company_email,
      telegram: orgForm.telegram,
      max_contact: orgForm.max_contact,
      whatsapp: orgForm.whatsapp,
      // KAN-783: lead attributes; an older backend without the field gets nothing
      lead_attributes: leadAttributes ? prepareAttributes(leadAttributes) : undefined,
      // KAN-811: an older backend without the field gets nothing
      manager_visibility: orgForm.manager_visibility,
    });
  }, [orgForm, updateOrganizationMutation]);

  const handleSaveIntegrations = useCallback(() => {
    // Validate webhook URL
    if (integrationsForm.webhook_url) {
      try {
        new URL(integrationsForm.webhook_url);
      } catch {
        setWebhookUrlError('Введите корректный URL');
        return;
      }
    }
    updateIntegrationsMutation.mutate({
      openrouter_api_key: integrationsForm.openrouter_api_key,
      telegram_bot_token: integrationsForm.telegram_bot_token,
      telegram_bot_enabled: integrationsForm.telegram_bot_enabled,
      webhook_url: integrationsForm.webhook_url,
      webhook_secret: integrationsForm.webhook_secret,
    });
  }, [integrationsForm, updateIntegrationsMutation]);

  const handleSaveSystem = useCallback(() => {
    // Validate pagination size (between 10 and 100)
    if (
      systemForm.pagination_size !== undefined &&
      (systemForm.pagination_size < 10 || systemForm.pagination_size > 100)
    ) {
      setPaginationError('Размер страницы должен быть от 10 до 100');
      return;
    }
    updateSystemMutation.mutate({
      language: systemForm.language,
      timezone: systemForm.timezone,
      date_format: systemForm.date_format,
      currency: systemForm.currency,
      pagination_size: systemForm.pagination_size,
      enable_notifications: systemForm.enable_notifications,
      enable_audit_log: systemForm.enable_audit_log,
    });
  }, [systemForm, updateSystemMutation]);

  // Determine if user can manage settings
  const userCanManage = settings?.user_can_manage !== false;

  // Current user — for role-based visibility (funnel tab is OWNER-only).
  const { data: currentUser } = useQuery<{ role?: string }>({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/auth/me/');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const isOwner = currentUser?.role === 'OWNER';
  // KAN-931: вкладка MAX только тем, кому открыт список аккаунтов MAX.
  const canSeeMax = useCanCallApi('GET', MAX_ACCOUNTS_URL);

  // Helper to render tab content based on loading/error states
  const renderTabContent = (tabName: string) => {
    if (isLoading) {
      return <LoadingSpinner />;
    }
    if (isError) {
      return <ErrorState onRetry={refetch} />;
    }

    switch (tabName) {
      case 'organization':
        return (
          <OrganizationSettings
            settings={orgForm as OrgSettingsType}
            onChange={handleOrgChange}
            onSave={handleSaveOrganization}
            onLogoUpload={handleLogoUpload}
            logoUploading={uploadLogoMutation.isPending}
            disabled={!userCanManage}
            nameError={orgNameError}
          />
        );
      case 'integrations':
        return (
          <IntegrationSettings
            settings={integrationsForm as IntegrationsSettingsType}
            onChange={handleIntegrationsChange}
            onSave={handleSaveIntegrations}
            disabled={!userCanManage}
            focusMailbox={focusMailbox}
          />
        );
      case 'system':
        return (
          <SystemSettings
            settings={systemForm as SystemSettingsType}
            onChange={handleSystemChange}
            onSave={handleSaveSystem}
            disabled={!userCanManage}
            paginationError={paginationError}
          />
        );
      // KAN-341: голосовой пакет и тарифы переехали в раздел «Биллинг».
      // Копию-вкладку здесь не оставляем: два места, где видно один и тот же
      // тариф, однажды разойдутся, и клиент поверит не тому.
      default:
        return null;
    }
  };

  return (
    <>
      <PageLayout
        data-testid="settings-page"
        title="Настройки"
        subtitle={<span className="hidden sm:inline">Управление настройками организации и предпочтениями</span>}
        titleIcon={
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <SettingsIcon className="h-5 w-5 text-white" />
          </div>
        }
        breadcrumbs={[{ label: 'Главная', href: '/' }, { label: 'Настройки' }]}>
        {/* Read-only notice */}
        {!userCanManage && !isLoading && <ReadOnlyNotice />}

        {/* Settings Form Container */}
        <div data-testid="settings-form-container" className="bg-white rounded-lg shadow">
          <Tabs
            data-testid="settings-tabs"
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full">
            <div className="border-b border-gray-200 px-4 sm:px-6 pt-4 overflow-x-auto">
              <TabsList className="bg-transparent gap-4 flex-nowrap">
                <TabsTrigger
                  data-testid="tab-organization"
                  value="organization"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3">
                  Организация
                </TabsTrigger>
                <TabsTrigger
                  data-testid="tab-integrations"
                  value="integrations"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3">
                  Интеграции
                </TabsTrigger>
                {isOwner && (
                  <TabsTrigger
                    data-testid="tab-funnel"
                    value="funnel"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3">
                    Воронка
                  </TabsTrigger>
                )}
                <TabsTrigger
                  data-testid="tab-telephony"
                  value="telephony"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3">
                  Телефония
                </TabsTrigger>
                <TabsTrigger
                  data-testid="tab-telegram"
                  value="telegram"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3">
                  Telegram-аккаунты
                </TabsTrigger>
                {/* KAN-931: аккаунты мессенджера MAX */}
                {canSeeMax && (
                  <TabsTrigger
                    data-testid="tab-max"
                    value="max"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3">
                    MAX
                  </TabsTrigger>
                )}
                <TabsTrigger
                  data-testid="tab-proxies"
                  value="proxies"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3">
                  Прокси
                </TabsTrigger>
                {false && (
                  <TabsTrigger
                    data-testid="tab-system"
                    value="system"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3">
                    Система
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

            {/* Organization Settings Tab */}
            <TabsContent value="organization" className="p-4 sm:p-6">
              {renderTabContent('organization')}
            </TabsContent>

            {/* Integrations Tab */}
            <TabsContent value="integrations" className="p-4 sm:p-6">
              {renderTabContent('integrations')}
            </TabsContent>

            {/* Funnel Config Tab (TASK-238) — OWNER only */}
            {isOwner && (
              <TabsContent value="funnel" className="p-4 sm:p-6">
                <FunnelConfig canManage={isOwner} />
              </TabsContent>
            )}

            {/* Бывшие разделы меню: сами страницы без своей шапки */}
            <TabsContent value="telephony" className="p-4 sm:p-6">
              <EmbeddedPage>
                <TelephonyPage />
              </EmbeddedPage>
            </TabsContent>
            <TabsContent value="telegram" className="p-4 sm:p-6">
              <EmbeddedPage>
                <TelegramAccountsPage />
              </EmbeddedPage>
            </TabsContent>
            {canSeeMax && (
              <TabsContent value="max" className="p-4 sm:p-6">
                <MaxAccountsSection />
              </TabsContent>
            )}
            {/* KAN-713: справочник прокси Telegram-аккаунтов */}
            <TabsContent value="proxies" className="p-4 sm:p-6">
              <ProxiesBlock />
            </TabsContent>

            {/* System Settings Tab */}
            <TabsContent value="system" className="p-4 sm:p-6">
              {renderTabContent('system')}
            </TabsContent>

          </Tabs>
        </div>
      </PageLayout>

      {/* Unsaved Changes Dialog */}
      <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <DialogContent data-testid="unsaved-changes-dialog">
          <DialogHeader>
            <DialogTitle>Несохранённые изменения</DialogTitle>
            <DialogDescription>У вас есть несохранённые изменения. Вы хотите их отменить?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnsavedDialog(false)}>
              Отмена
            </Button>
            <Button data-testid="discard-changes-button" variant="destructive" onClick={handleDiscardChanges}>
              Отменить изменения
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
